import React, { useEffect, useState } from 'react';
import BaseTile from '../../../components/Tile/BaseTile';
import TileContent from '../../../components/Tile/TileContent';
import { useGlobalAPI } from '../../../hooks/useGlobalAPI';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

/**
 * AvgEventsPerObjectType Component
 *
 * Displays average events per object for each object type using a bar chart.
 * Fully integrated with the global filter system - automatically re-fetches
 * when filters change.
 *
 * @param {string} title - Tile title (default: 'Avg. Events per Object Type')
 * @param {string} activeFile - Active OCEL file type (required for filter integration)
 */
const AvgEventsPerObjectType = ({ title = 'Avg. Events per Object Type', activeFile = 'main' }) => {
  const api = useGlobalAPI(activeFile);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!activeFile) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        // Use POST endpoint for filtered data with global filters automatically applied
        const response = await api.post('/kpi/object-type-event-intensity', {});

        // Handle response format: { file_type, items: [...] }
        const items = response?.items || [];
        const formatted = items.map(item => ({
          type: String(item.object_type),
          avg: Number(item.avg_events_per_object)
        }));

        if (!cancelled) setData(formatted);
      } catch (e) {
        console.error('Error fetching avg events per object type:', e);
        if (!cancelled) setError(e?.message || 'Failed to load data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [activeFile, api.currentFilters]); // Re-fetch when activeFile or filters change

  return (
    <BaseTile title={title} loading={loading} error={error} minHeight="lg" padding="default">
      <TileContent>
        {(!loading && !error && data.length === 0) ? (
          <div className="text-sm text-gray-600 text-center py-6">No data available for the current dataset/configuration.</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data} margin={{ top: 20, right: 20, bottom: 20, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="type" interval={0} angle={-20} textAnchor="end" height={60} />
              <YAxis allowDecimals />
              <Tooltip formatter={(v) => (Number.isFinite(v) ? Number(v).toFixed(2) : v)} />
              <Bar dataKey="avg" fill="#4f46e5" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </TileContent>
    </BaseTile>
  );
};

export default AvgEventsPerObjectType;
